<template>
    <div>
        <div class="l-inline l-center-v l-gap-2 filter-actions">
            <div>
                <i18n path="showOf">
                    <b>{{ filteredTrucks.length }}</b>
                    <b>{{ trucks.length }}</b>
                </i18n>
            </div>
            <div class="l-grow-1">
                <slot name="filter" />
            </div>
            <div v-if="hasLsvaAdminAccess">
                <BaseButton size="small" @click="handleRegisterNew">
                    {{ $t('registerNew') }}
                </BaseButton>
            </div>
        </div>

        <div v-if="isTrucksLoading" class="l-inline l-center-v l-center">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <table v-else>
            <thead>
                <tr>
                    <th>{{ $t('shared.lsva.columns.type') }}</th>

                    <th>
                        {{ $t('shared.lsva.columns.name') }}

                        <IconButton @click="handleSort('name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'name' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('shared.lsva.columns.vin') }}

                        <IconButton @click="handleSort('vin')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'vin' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('shared.lsva.columns.identification') }}

                        <IconButton @click="handleSort('identification')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'identification'
                                        ? sortDirection
                                        : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('shared.lsva.columns.drives') }}

                        <IconButton @click="handleSort('leg_count')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'leg_count' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('shared.lsva.columns.endOfRegistration') }}

                        <IconButton @click="handleSort('end_date')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'end_date' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('shared.lsva.columns.message') }}

                        <IconButton @click="handleSort('declaration_type')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'declaration_type'
                                        ? sortDirection
                                        : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>
                    <th>&nbsp;</th>
                </tr>
            </thead>

            <tr
                v-for="item in filteredTrucks"
                :key="item.id"
                :class="{ selected: selectedTruck === item }"
                @click="handleTruckClick(item)"
            >
                <td>
                    <BaseAvatar
                        :src="item.toll_declaration.truck.marker"
                        :color="item.toll_declaration.truck.color"
                        :size="32"
                    />
                </td>

                <td>
                    {{ item.toll_declaration.truck.name }}
                </td>

                <td>
                    {{ item.toll_declaration.truck.vin }}
                </td>

                <td>
                    {{ item.toll_declaration.truck.identification }}
                </td>

                <td>
                    {{ $tc('trips', item.leg_count) }}
                </td>

                <td>
                    {{ item.toll_declaration.truck.end_date || $t('noEnd') }}
                </td>

                <td>
                    {{ getItemStatus(item) }}
                </td>

                <td>
                    <LsvaDeclarationStatusBadge :declaration="item" />
                </td>
            </tr>
        </table>

        <div
            v-if="!isTrucksLoading && !filteredTrucks.length"
            class="l-padded l-inline l-center t-subtle t-small"
        >
            <span>
                {{ $t('shared.noSearchResult') }}
            </span>
        </div>
    </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import VSpinner from 'vue-simple-spinner'

import BaseAvatar from '@/components/base/BaseAvatar'
import IconButton from '@/components/IconButton'
import LsvaDeclarationStatusBadge from '@/components/lsva/LsvaDeclarationStatusBadge'
import SortArrowIcon from '@/components/icons/SortArrowIcon'
import BaseButton from '@/components/redesigned/BaseButton.vue'

import LsvaTruckRegisterModal from '@/components/lsva/LsvaTruckRegisterModal'

const defaultSortColumn = 'name'
const defaultSortDirection = 1

export default {
    name: 'LsvaOverviewTrucksTableView',
    components: {
        BaseButton,
        BaseAvatar,
        IconButton,
        LsvaDeclarationStatusBadge,
        SortArrowIcon,
        VSpinner,
    },
    props: {
        filterSearch: {
            type: String,
            default: '',
        },
        filterStatus: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            searchQuery: '',
            searchStatus: '',
            sortBy: defaultSortColumn,
            sortDirection: defaultSortDirection,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
        }
    },
    computed: {
        ...mapState('lsva', ['isTrucksLoading', 'selectedTruck', 'trucks']),
        ...mapGetters('auth', ['hasLsvaAdminAccess']),
        filteredTrucks() {
            return this.trucks
                .map(truck => ({
                    ...truck,
                    leg_count:
                        (truck.gnss_without_trip ||
                            truck.declaration_type === 'GNSS_WITHOUT_TRIP') &&
                        truck.leg_count === 1
                            ? 0
                            : truck.leg_count,
                }))
                .filter(truck => {
                    const search = this.searchQuery.toLowerCase()
                    const status = this.searchStatus

                    return (
                        (search === '' ||
                            truck.toll_declaration.truck.name
                                .toLowerCase()
                                .includes(search) ||
                            truck.toll_declaration.truck.vin
                                .toLowerCase()
                                .includes(search)) &&
                        (status === '' ||
                            truck.declaration_type === status ||
                            (status === 'GNSS_WITHOUT_TRIP' &&
                                truck.declaration_type === 'GNSS' &&
                                truck.gnss_without_trip === true) ||
                            (status === 'GNSS_WITH_TRIP' &&
                                truck.declaration_type === 'GNSS' &&
                                truck.gnss_without_trip === false) ||
                            (status === 'GPS_ISSUE' &&
                                truck.needs_manual_correction_cause ===
                                    'GPS_ISSUE'))
                    )
                })
                .sort((a, b) => {
                    const aField = [
                        'end_date',
                        'identification',
                        'name',
                        'vin',
                    ].includes(this.sortBy)
                        ? a.toll_declaration.truck[this.sortBy] || ''
                        : a[this.sortBy]

                    const bField = [
                        'end_date',
                        'identification',
                        'name',
                        'vin',
                    ].includes(this.sortBy)
                        ? b.toll_declaration.truck[this.sortBy] || ''
                        : b[this.sortBy]

                    if (aField === bField) {
                        return 0
                    } else if (aField == null) {
                        return 1
                    } else if (bField == null) {
                        return -1
                    } else if (typeof aField === 'string') {
                        return (
                            aField.localeCompare(bField, undefined, {
                                numeric: true,
                            }) * this.sortDirection
                        )
                    }

                    return aField > bField
                        ? this.sortDirection
                        : aField < bField
                        ? -this.sortDirection
                        : 0
                })
        },
    },
    watch: {
        '$route.query.search': {
            handler(value) {
                this.searchQuery = value || ''
            },
            immediate: true,
        },
        '$route.query.status': {
            handler(value) {
                this.searchStatus = value || ''
            },
            immediate: true,
        },
        '$route.query.selected_declaration'(id) {
            this.setSelectedTruckById(id)
        },
        isTrucksLoading(newVal, oldVal) {
            if (
                newVal === false &&
                oldVal === true &&
                this.$route.query.selected_declaration
            ) {
                this.setSelectedTruckById(
                    this.$route.query.selected_declaration
                )
            }
        },
    },
    mounted() {
        this.loadTrucks()
    },
    methods: {
        ...mapActions('lsva', ['setSelectedTruckById', 'loadTrucks']),
        getItemStatus(item) {
            if (
                item.declaration_type === 'GNSS' &&
                item.gnss_without_trip === true
            ) {
                return this.$t(`shared.lsva.truckStatus.GNSS_WITHOUT_TRIP`)
            }

            if (
                item.declaration_type === 'GNSS' &&
                item.gnss_without_trip === false
            ) {
                return this.$t(`shared.lsva.truckStatus.GNSS_WITH_TRIP`)
            }

            return this.$t(`shared.lsva.truckStatus.${item.declaration_type}`)
        },
        handleTruckClick(truck) {
            const params = {}
            const query = {}

            if (this.$route.params.date) {
                params.date = this.$route.params.date
            }

            if (truck.toll_declaration.truck.id) {
                query.selected_declaration = truck.toll_declaration.truck.id
            }

            this.$router.push({
                name: 'lsvaDeclarations',
                params,
                query: { ...this.$route.query, ...query },
            })
        },
        handleSort(column) {
            if (this.sortBy !== column || this.sortDirection !== 1) {
                this.sortBy = column
                this.sortDirection = 1
            } else if (this.sortDirection === 1) {
                this.sortDirection = -1
            }
        },
        handleRegisterNew() {
            this.$modal.show(
                LsvaTruckRegisterModal,
                { onSubmit: this.loadTrucks },
                { width: 400, height: '75%' }
            )
        },
    },
}
</script>

<i18n>
{
    "en": {
        "clearFilter": "Clear filter",
        "registerNew": "Register New",
        "showOf": "Show {0} of {1}",
        "trackingError": "Tracking error",
        "noEnd": "No end",
        "trips": "no trips | {n} trip | {n} trips"
    },
    "de": {
        "clearFilter": "Filter zurücksetzen",
        "registerNew": "Neuregistrierung",
        "showOf": "Zeige {0} von {1}",
        "trackingError": "Trackingfehler",
        "noEnd": "Kein Ende",
        "trips": "keine Fahrt | {n} Fahrt | {n} Fahrten"
    },
    "fr": {
        "clearFilter": "Supprimer le filtre",
        "registerNew": "Nouvelle immatriculation",
        "showOf": "Afficher {0} de {1}",
        "trackingError": "Erreur de suivi",
        "noEnd": "Pas de fin",
        "trips": "aucun trajet | {n} trajet | {n} trajets"
    },
    "it": {
        "clearFilter": "Annullare il filtro",
        "registerNew": "Registrazione nuova",
        "showOf": "Listo {0} di {1}",
        "trackingError": "Errore di tracciamento",
        "noEnd": "Nessuna fine",
        "trips": "nessun viaggio | {n} viaggio | {n} viaggi"
    }
}
</i18n>

<style lang="scss" scoped>
.filter-actions {
    padding: 1rem 2rem;
    background-color: #fff;
    font-size: 16px;
    color: rgba(0, 0, 0, 0.7);
    z-index: 1;
}

table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: 0;
        background-color: #fff;
        z-index: 1;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        height: 100%;

        &.selected {
            background-color: $color-gray-lighter;
        }

        &:not(:first-child) {
            cursor: pointer;

            &:hover {
                background-color: $color-gray-lighter;
            }

            & + tr {
                td {
                    border-top: $style-border;
                }
            }
        }

        th {
            padding: 1rem;
            border-top: $style-border;
            border-bottom: $style-border;
            text-align: left;

            &.action {
                padding-top: 0;
                padding-bottom: 0;
                font-weight: normal;

                & > * {
                    width: 240px;
                }
            }

            &.dynamic {
                cursor: grab;
                transition: box-shadow 0.1s;

                &:active {
                    cursor: grabbing;
                }

                &:hover {
                    box-shadow: 0 0 4px rgba(0, 0, 0, 0.08);
                }

                &.sortable-chosen,
                &.sortable-ghost {
                    background-color: #fff;
                    box-shadow: 0 0 8px rgba(0, 0, 0, 0.08);
                }
            }
        }

        td {
            padding: 0.5rem 1rem;
        }

        th,
        td {
            white-space: nowrap;

            &:first-child {
                padding-left: 2rem;
            }

            &:last-child {
                padding-right: 2rem;
            }

            &:not(:last-child) {
                width: 1%;
            }
        }
    }
}
</style>
